<template>
  <div v-loading="availableDocutaxClients.loading" class="dialog-container">
    <div class="el-form-item">
      <label class="el-form-item__label">Select Existing Docutax Client</label>
      <el-select size="small" filterable style="width: 100%" v-model="selectedDocutaxClientId" clearable
        placeholder="Select a Docutax Client" @clear="onDocutaxClientClear">
        <el-option v-for="client in availableDocutaxClients.data" :key="client.id" :label="client.toString()"
          :value="client.id">
        </el-option>
      </el-select>
    </div>
    <p v-show="isNewClient">or enter new client</p>
    <el-form size="small" :model="form" status-icon :rules="validationRules" ref="requestForm">
      <el-form-item v-show="isNewClient" label="First Name" required prop="client.firstName">
        <el-input :disabled="!isNewClient" v-model="form.client.firstName" placeholder="First Name" autocomplete="off"
          data-lpignore="true" data-form-type="other" />
      </el-form-item>
      <el-form-item v-show="isNewClient" label="Last Name" required prop="client.lastName">
        <el-input :disabled="!isNewClient" v-model="form.client.lastName" placeholder="Last Name" autocomplete="off"
          data-lpignore="true" data-form-type="other" />
      </el-form-item>
      <el-form-item v-show="isNewClient" label="Cell Phone" required prop="client.cellPhone">
        <el-input :disabled="!isNewClient" v-model="form.client.cellPhone" type="text" placeholder="Cell Phone"
          v-mask="'(###) ###-####'" inputmode="tel" autocomplete="off" data-lpignore="true" data-form-type="other" />
      </el-form-item>
      <el-alert type="error" v-if="existingClientWithSamePhoneNumber" :closable="false">
        <h1 slot="title">Error: Client already exists with this phone number!</h1>
        <h2> {{ existingClientWithSamePhoneNumber.toString() }}</h2>
        <b>Assigned to Office EFIN:</b> {{ existingClientWithSamePhoneNumber.selectedOfficeEfin }}<br />

        <h2>How would you like to proceed?</h2>
        <el-button size="small" type="primary" @click="sendRequestToExistingClientWithSamePhoneNumber">Send Request to
          this Client</el-button>
        <el-button size="small" type="danger" plain @click="requestTransfer">Request Transfer to my Office</el-button>
      </el-alert>
      <el-form-item v-show="isNewClient" label="Email Address (optional)" prop="client.emailAddress">
        <el-input :disabled="!isNewClient" v-model="form.client.emailAddress" type="email"
          placeholder="Email Address (optional)" autocomplete="off" data-lpignore="true" data-form-type="other" />
      </el-form-item>
      <el-form-item label="Office" required prop="officeId">
        <el-select style="width: 100%" v-model="form.officeId" :clearable="false" placeholder="Select Office">
          <el-option v-for="item in allUserOffices" :key="item.id" :label="'#' + item.storeNum + ' - ' + item.address"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Message" required prop="message">
        <el-input v-model="form.message" type="textarea" maxlength="500" :autosize="{ minRows: 3, maxRows: 6 }"
          placeholder="Include a message to your client" />
      </el-form-item>
      <docutax-request-message-template :docutax-client="form.client" @change="onTemplateChange"
        @clear="onTemplateClear" />
      <el-row type="flex" justify="center">
        <el-button v-loading="saving" class="ma-3" type="primary" round @click="onSubmit">Send Request</el-button>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '@/api';
import { DocutaxClient } from '@/model';
import DocutaxRequestMessageTemplate from '@/components/form-item/DocutaxRequestMessageTemplate.vue';

export default {
  components: {
    DocutaxRequestMessageTemplate,
  },
  props: {
    docutaxClientId: {
      type: Number,
      required: false,
    },
    threepClient: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      saving: false,
      form: {
        client: new DocutaxClient(),
        message: '',
        officeId: undefined,
      },
      selectedDocutaxClientId: undefined,
      availableDocutaxClients: {
        loading: false,
        data: [],
      },
      existingClientPhoneNumberLookup: {
        loading: false,
        phone: undefined,
        client: undefined,
      },
      validationRules: {
        'client.firstName': [{ required: true, message: 'First Name is required', trigger: 'blur' }],
        'client.lastName': [{ required: true, message: 'Last Name is required', trigger: 'blur' }],
        'client.cellPhone': [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('Cell phone number is required.'));
              } else if (!value.match(/^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$/i)) {
                callback(new Error('Please enter a valid phone number in (999) 999-9999 format'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        'client.emailAddress': [
          {
            required: false,
            validator: (rule, value, callback) => {
              if (!value) {
                callback();
              } else if (!value.match(/^.+@.+\..+$/i)) {
                callback(new Error('Please enter a valid email address'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        officeId: [{ required: true }],
        message: [{ required: true }],
      },
    };
  },
  watch: {
    docutaxClientId: {
      immediate: true,
      handler(val) {
        this.selectedDocutaxClientId = val;
      },
    },
    selectedDocutaxClient: {
      immediate: true,
      handler(val, oldVal) {
        if (val == null && oldVal == null) {
          return;
        }

        if (val == null) {
          this.form.client = new DocutaxClient();
          return;
        }

        this.form.client = new DocutaxClient(val);
        this.form.officeId = this.form.client.selectedOfficeId;
      },
    },
    threepClient: {
      immediate: true,
      handler(val, oldVal) {
        if (val == null && oldVal == null) {
          return;
        }

        if (val == null) {
          this.form.client = new DocutaxClient();
          return;
        }

        this.form.client = DocutaxClient.initFromThreePClient(val);
      },
    },
    'form.client.cellPhone': {
      async handler(phoneNumber) {
        if (!this.isNewClient) {
          return;
        }

        try {
          await this.isCellPhoneFieldValid();
        } catch (e) {
          return;
        }

        this.checkForExistingClientWithSamePhoneNumber(phoneNumber);
      },
    },
  },
  computed: {
    ...mapGetters('User', ['allUserOffices']),
    selectedDocutaxClient() {
      if (this.selectedDocutaxClientId == null) {
        return undefined;
      }

      return this.availableDocutaxClients.data.find(client => client.id === this.selectedDocutaxClientId);
    },
    isNewClient() {
      return this.form.client.id == null && this.form.client.threePClientId == null;
    },
    existingClientWithSamePhoneNumber() {
      if (this.isNewClient && this.existingClientPhoneNumberLookup.client && this.existingClientPhoneNumberLookup.phone === this.form.client.cellPhone) {
        return this.existingClientPhoneNumberLookup.client;
      }

      return null;
    },
  },
  created() {
    this.loadAvailableDocutaxClients();
  },
  methods: {
    async onSubmit() {
      try {
        await this.$refs.requestForm.validate();
      } catch (e) {
        // form not valid
        return;
      }

      try {
        this.saving = true;
        const response = await api.docutaxRequests.create(this.form);
        if (response.data) {
          this.$emit('requestSent');
          this.onClose();
        } else {
          throw new Error('An unknown error occurred');
        }
      } catch (e) {
        console.error('An error occurred while sending docutax request', e);
        let message = e.message;
        if (e.response && e.response.data) {
          message = e.response.data;
        }
        this.$message.error(`Failed to send docutax request - ${message}`);
      } finally {
        this.saving = false;
      }
    },
    async loadAvailableDocutaxClients() {
      try {
        this.availableDocutaxClients.loading = true;
        const response = await api.docutaxRequests.findAllAvailableClients();
        if (response.data) {
          this.availableDocutaxClients.data = response.data.map(val => new DocutaxClient(val));
        }
      } catch (e) {
        this.$message.error('Failed to load available Docutax Clients');
      } finally {
        this.availableDocutaxClients.loading = false;
      }
    },
    onTemplateChange(templateContent) {
      this.form.message = templateContent;
    },
    onTemplateClear() {
      this.form.message = '';
    },
    onClose() {
      this.onDocutaxClientClear();
      this.$emit('close');
    },
    onDocutaxClientClear() {
      this.selectedDocutaxClientId = undefined;
      this.$nextTick(() => {
        this.form.client = new DocutaxClient();
        this.$refs.requestForm.resetFields();
      });
      this.$emit('update:docutaxClientId', undefined);
    },
    isCellPhoneFieldValid() {
      return new Promise((resolve, reject) => {
        this.$refs.requestForm.validateField('client.cellPhone', (errs) => {
          if (errs) {
            reject(errs);
          }

          resolve(true);
        });
      });
    },
    async checkForExistingClientWithSamePhoneNumber(phone) {
      if (this.existingClientPhoneNumberLookup.phone === phone
        && (
          this.existingClientPhoneNumberLookup.loading // in flight
          || this.existingClientPhoneNumberLookup.client !== undefined // already completed
        )) {
        console.info('bypassing lookup because it is in flight or has already completed for phone');
        return;
      }

      try {
        this.existingClientPhoneNumberLookup.loading = true;
        this.existingClientPhoneNumberLookup.phone = phone;

        const result = await api.docutaxRequests.findClientByPhoneNumber(phone);
        if (result && result.data) {
          this.existingClientPhoneNumberLookup.client = new DocutaxClient(result.data);
        } else {
          this.existingClientPhoneNumberLookup.client = null; // explicitly null instead of undefined to signify the lookup has completed
        }
      } catch (e) {
        console.error('failed to lookup existing client with phone', e);
        this.existingClientPhoneNumberLookup.client = null;
      } finally {
        this.existingClientPhoneNumberLookup.loading = false;
      }
    },
    sendRequestToExistingClientWithSamePhoneNumber() {
      const clientIsInExistingContactsList = this.availableDocutaxClients.data.some(client => client.id === this.existingClientWithSamePhoneNumber.id);
      if (!clientIsInExistingContactsList) {
        this.availableDocutaxClients.data.push(new DocutaxClient(this.existingClientWithSamePhoneNumber));
      }

      this.selectedDocutaxClientId = this.existingClientWithSamePhoneNumber.id;
    },
    requestTransfer() {
      this.$alert(`Please call support to request a transfer for ${this.existingClientWithSamePhoneNumber.firstName} ${this.existingClientWithSamePhoneNumber.lastName}`, 'Request Transfer', {
        confirmButtonText: 'Done',
      });
    },
  },
};
</script>
